import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import {getUserId} from '@Libraries/user.library';
import type {GridUserImageItemStorage} from '@Components/base-grid/components/grid-user-image-item';
import {getGridUserImageItemStorageForUserImageVO} from '@Components/base-grid/components/grid-user-image-item';
import {openErrorModal} from '@Modals/error-modal';
import type {Dimensions} from '@floating-ui/react';
import type {UserImageVOResponse} from '@Libraries/user-image-library';
import {checkUploadStatus, UserMediaType} from '@Libraries/user-media-library';
import {isIOSAppOrEmbeddedEditor} from '@Utils/browser.util';
import {deleteCookie, readCookie} from '@Utils/cookie.util';
import {getStateFromUrl} from '@Utils/url.util';
import type * as GooglePicker from '../types/google-picker';

const OAUTH_DATA_COOKIE = 'pmwsac';

/**
 * Max height of Google picker are mentioned at https://developers.google.com/picker/docs/reference
 */
const PICKER_MAX_HEIGHT = 650;
/**
 * Max width of Google picker are mentioned at https://developers.google.com/picker/docs/reference
 */
const PICKER_MAX_WIDTH = 1051;
const PICKER_PADDING = 40;
const LOGIN_WINDOW_WIDTH = 500;
const LOGIN_WINDOW_HEIGHT = 570;
let init = false;
let googlePicker: GooglePicker.picker.Picker;

let pickerApiLoaded = false;

let oauthToken: string;

let apiKey: string;

let oauthUrl: string;

let onAddSelected: (item: Array<GridUserImageItemStorage>, currentUploadIndex?: number, totalUploadsCount?: number) => void;
let onStartAdd: () => void;

export const openGooglePicker = async (
  url: string,
  onAddItem: (item: Array<GridUserImageItemStorage>, currentUploadIndex?: number, totalUploadsCount?: number, onStartAddSelected?: () => void) => void,
  onStartAddSelected: () => void
): Promise<void> => {
  oauthUrl = url;
  onAddSelected = onAddItem;
  onStartAdd = onStartAddSelected;
  if (init) {
    if (arePickerDimensionsValid()) {
      setPickerVisible();
    } else {
      createPicker();
    }
  } else {
    await initialize();
  }
};

const initialize = async (): Promise<void> => {
  try {
    await initGoogleLogin();
    showLoading('loadGoogleDrive', {
      text: window.i18next.t('pmwjs_loading_google_photos'),
      hideIcon: true,
    });
    window.PMW.google.loadFeaturesAPI(onLoggedIn);
  } catch (e) {
    hideLoading('loadGoogleDrive');
    openErrorModal();
  }
};

const calculatePickerDimensions = (): Dimensions => {
  return {
    width: window.innerWidth > PICKER_MAX_WIDTH ? PICKER_MAX_WIDTH : window.innerWidth - PICKER_PADDING,
    height: window.innerHeight > PICKER_MAX_HEIGHT ? PICKER_MAX_HEIGHT : window.innerHeight - PICKER_PADDING,
  };
};

const arePickerDimensionsValid = (): boolean => {
  if (googlePicker) {
    const dimensions = calculatePickerDimensions();
    return !(Math.abs(dimensions.width - googlePicker.jha) > 10 || Math.abs(dimensions.height - googlePicker.iha) > 10);
  }
  return false;
};

const onLoggedIn = (): void => {
  createPicker();

  if (typeof window.gapi !== 'undefined') {
    window.gapi.load('picker', {callback: onPickerApiLoad});
  } else {
    window.PMW.google.onError();
  }
};

const onPickerApiLoad = (): void => {
  pickerApiLoaded = true;
  if (!googlePicker && oauthToken) {
    createPicker();
  }
};

const createPicker = (): void => {
  if (pickerApiLoaded && oauthToken) {
    const allowedMimeType = 'image/jpeg,image/png,image/gif';
    const pickerDimensions = calculatePickerDimensions();
    const language = window.PMW.util.getLanguage();
    const viewMyDrive = new window.google.picker.DocsView().setIncludeFolders(true).setOwnedByMe(true).setMimeTypes(allowedMimeType);
    const viewSharedWithMe = new window.google.picker.DocsView().setIncludeFolders(true).setOwnedByMe(false).setMimeTypes(allowedMimeType);
    const recentlyPickedView = new window.google.picker.DocsView(window.google.picker.ViewId.RECENTLY_PICKED);

    googlePicker = new window.google.picker.PickerBuilder()
      .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
      .addView(viewMyDrive)
      .addView(viewSharedWithMe)
      .addView(recentlyPickedView)
      .setLocale(language)
      .setSize(pickerDimensions.width, pickerDimensions.height)
      .setTitle(window.i18next.t('pmwjs_select_a_photo'))
      .setOAuthToken(oauthToken)
      .setDeveloperKey(apiKey)
      .setSelectableMimeTypes(allowedMimeType)
      .setCallback((data: GooglePicker.picker.ResponseObject) => {
        void pickerCallback(data);
      })
      .build();

    setPickerVisible();
    hideLoading('loadGoogleDrive');
  }
};

const setPickerVisible = (): void => {
  if (googlePicker) {
    googlePicker.setVisible(true);
  }
};

const pickerCallback = async (data: GooglePicker.picker.ResponseObject): Promise<void> => {
  if (data.action === window.google.picker.Action.PICKED) {
    onStartAdd();

    const docs = data[window.google.picker.Response.DOCUMENTS];
    let error = false;

    showLoading('uploadGoogleDriveItems');
    for (let i = 0; i < docs.length; i++) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const imageData = (await window.PMW.pollingAjaxCallAsync(
          () => {
            return window.PMW.writeLocal('posterimage/importGoogleDrivePhoto', {
              userId: getUserId(),
              id: docs[i].id,
              lastEdited: docs[i].lastEditedUtc,
              oauthToken,
            });
          },
          checkUploadStatus.bind(null, UserMediaType.IMAGE)
        )) as UserImageVOResponse;

        onAddSelected([getGridUserImageItemStorageForUserImageVO(imageData)], i + 1, docs.length);
      } catch (e) {
        console.error(e);
        error = true;
      }
    }

    if (error) {
      openErrorModal({
        message: window.i18next.t('pmwjs_general_ajax_error'),
      });
    }
    hideLoading('uploadGoogleDriveItems');
  }
};

const initGoogleLogin = async (): Promise<void> => {
  const loginWindow = window.open(oauthUrl, '_blank', `width=${LOGIN_WINDOW_WIDTH},height=${LOGIN_WINDOW_HEIGHT}`);
  if (isIOSAppOrEmbeddedEditor()) {
    await checkLoggedInStatus();
    return;
  }
  return new Promise((resolve, reject) => {
    const interval = setInterval(() => {
      const cookie = readCookie(OAUTH_DATA_COOKIE);
      if (cookie) {
        clearInterval(interval);
        deleteCookie(OAUTH_DATA_COOKIE);
        loginWindow?.close();
        const response = JSON.parse(decodeURIComponent(cookie));
        if (response.status === 'success') {
          oauthToken = response.data.token;
          apiKey = response.data.apiKey;
          init = true;
          resolve();
        } else {
          reject();
        }
      }
    }, 500);
  });
};

const checkLoggedInStatus = async (): Promise<void> => {
  const response = await window.PMW.pollingAjaxCallAsync(
    async () => {},
    () => {
      return window.PMW.readLocal('authenticate/hasAccountBeenLoggedIn', {
        state: getStateFromUrl(oauthUrl ?? ''),
      });
    }
  );
  if (response.status === 'success') {
    oauthToken = response.data.token;
    apiKey = response.data.apiKey;
    init = true;
  }
};
